import {
  createEmptyProductLabel,
  parseProductLabel,
  serializeProductLabel,
} from 'features/Procedure/features/treatments/components/product-scanner/product-scanner.utils'
import type { EmrCodeItem, ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'

import type { FormValues } from './schema-product'

type ProductLabelsInput = string[] | string | null | undefined

export const defaultInitialValues: FormValues = {
  cpt4_code: null,
  icd10_codes: [],
  procedure_units: undefined,
  units: 1,
  price: 0,
  upc: '',
  discount_type_id: null,
}

const parseJustifyCodes = (justifyCodes: string[]): EmrCodeItem[] => {
  return justifyCodes
    .filter((code) => code.startsWith(ProcedureTypeCode.ICD10))
    .map((code) => {
      const [, icd10Code] = code.split('|')
      return {
        code: icd10Code.replace(/\s+/g, ''),
        code_type: ProcedureTypeCode.ICD10,
        code_text: '',
        code_text_short: '',
        active: 1,
        id: 0,
      }
    })
}

export const formatJustifyCodes = (icd10Codes: EmrCodeItem[]): string[] => {
  return icd10Codes.map((icd10) => `${icd10.code_type}|${icd10.code.replace(/\s+/g, '')}`)
}

const normalizeProductLabels = (productLabels: ProductLabelsInput): string[] => {
  if (Array.isArray(productLabels)) {
    return productLabels
  }

  if (!productLabels) {
    return []
  }

  try {
    const parsedLabels = JSON.parse(productLabels)

    if (Array.isArray(parsedLabels)) {
      return parsedLabels
    }
  } catch {
    // Non-JSON labels are treated as one serialized label.
  }

  return [productLabels]
}

export const formatProductLabelsWithUpc = (
  productLabels: ProductLabelsInput,
  upc: string,
): string[] => {
  const [primaryLabel, ...remainingLabels] = normalizeProductLabels(productLabels)
  const normalizedUpc = upc.trim()

  if (!primaryLabel && !normalizedUpc) {
    return remainingLabels
  }

  const primaryFields = primaryLabel ? parseProductLabel(primaryLabel) : createEmptyProductLabel()

  return [
    serializeProductLabel({
      ...primaryFields,
      lot: normalizedUpc,
    }),
    ...remainingLabels,
  ]
}

const getUpcFromProductLabels = (productLabels: ProductLabelsInput): string => {
  const [primaryLabel] = normalizeProductLabels(productLabels)

  if (!primaryLabel) {
    return ''
  }

  return parseProductLabel(primaryLabel).lot
}

export const getInitialValues = (
  procedure?: ProcedureItem | null,
  defaultDiscountTypeId?: string | null,
): FormValues => {
  if (!procedure) {
    return {
      ...defaultInitialValues,
      discount_type_id: defaultDiscountTypeId || null,
    }
  }

  if (procedure.code_type !== ProcedureTypeCode.CPT4) {
    return {
      ...defaultInitialValues,
      discount_type_id: defaultDiscountTypeId || null,
    }
  }

  const cpt4CodeItem: EmrCodeItem = {
    code: procedure.code,
    code_type: procedure.code_type,
    code_text: procedure.code_text || '',
    code_text_short: '',
    active: 1,
    id: procedure.id,
  }

  const icd10CodeItems = parseJustifyCodes(procedure.justify_codes || [])

  return {
    cpt4_code: cpt4CodeItem,
    icd10_codes: icd10CodeItems,
    procedure_units: procedure.procedure_units || undefined,
    units: procedure.units,
    price: procedure.fee,
    upc: getUpcFromProductLabels(procedure.product_labels),
    discount_type_id: procedure.discount_type || defaultDiscountTypeId || null,
  }
}
